import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/services/api';

import { WorkflowStats, WorkflowList } from './workflows/WorkflowList';
import { WorkflowEditor } from './workflows/WorkflowEditor';

const EMPTY_FORM = {
  name: '', description: '', trigger_type: '', trigger_delay_minutes: 0,
  trigger_conditions: {}, actions: [], target_customer_types: [],
  target_cohorts: [], target_lifecycle_stages: [], is_active: false
};

const WorkflowsPanel = () => {
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchWorkflows = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/admin/workflows');
      if (response.data.success) setWorkflows(response.data.workflows);
    } catch { toast.error('Failed to load workflows'); }
    finally { setIsLoading(false); }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/templates');
      if (response.data.success) setTemplates(response.data.templates);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchWorkflows(); fetchTemplates(); }, [fetchWorkflows, fetchTemplates]);

  const resetForm = () => setFormData(EMPTY_FORM);

  const handleToggle = async (workflowId) => {
    try {
      const response = await api.post(`/api/admin/workflows/${workflowId}/toggle`);
      if (response.data.success) {
        setWorkflows(prev => prev.map(w => w.workflow_id === workflowId ? { ...w, is_active: response.data.is_active } : w));
        toast.success(response.data.is_active ? 'Workflow activated' : 'Workflow paused');
      }
    } catch { toast.error('Failed to toggle workflow'); }
  };

  const handleDelete = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;
    try {
      const response = await api.delete(`/api/admin/workflows/${workflowId}`);
      if (response.data.success) {
        setWorkflows(prev => prev.filter(w => w.workflow_id !== workflowId));
        toast.success('Workflow deleted');
      }
    } catch { toast.error('Failed to delete workflow'); }
  };

  const handleEdit = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      name: workflow.name, description: workflow.description || '',
      trigger_type: workflow.trigger_type, trigger_delay_minutes: workflow.trigger_delay_minutes || 0,
      trigger_conditions: workflow.trigger_conditions || {}, actions: workflow.actions || [],
      target_customer_types: workflow.target_customer_types || [],
      target_cohorts: workflow.target_cohorts || [],
      target_lifecycle_stages: workflow.target_lifecycle_stages || [],
      is_active: workflow.is_active
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.trigger_type) { toast.error('Name and trigger type are required'); return; }
    setIsSaving(true);
    try {
      if (editingWorkflow) {
        await api.put(`/api/admin/workflows/${editingWorkflow.workflow_id}`, formData);
        toast.success('Workflow updated');
      } else {
        await api.post('/api/admin/workflows', formData);
        toast.success('Workflow created');
      }
      setShowCreateModal(false);
      setEditingWorkflow(null);
      resetForm();
      fetchWorkflows();
    } catch { toast.error('Failed to save workflow'); }
    finally { setIsSaving(false); }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflows-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">Automated Workflows</h2>
          <p className="text-gray-500">Define triggers and automated actions for customer engagement</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingWorkflow(null); setShowCreateModal(true); }}>
          <Plus className="h-4 w-4 mr-2" />Create Workflow
        </Button>
      </div>

      <WorkflowStats workflows={workflows} />
      <WorkflowList workflows={workflows} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} onCreateNew={() => setShowCreateModal(true)} />

      <WorkflowEditor
        open={showCreateModal} onOpenChange={setShowCreateModal}
        formData={formData} setFormData={setFormData}
        templates={templates} editingWorkflow={editingWorkflow}
        onSave={handleSave} isSaving={isSaving}
      />
    </div>
  );
};

export default WorkflowsPanel;
